import type { CropRect } from '@pulsify/shared/dto'
import type { PostFormat } from './formats'

/**
 * Largest rect of `aspect` that fits the source, expressed as source fractions.
 * `offset` (0..1) slides it along whichever axis has slack; the other axis is
 * always fully used, so the frame never shows empty space.
 */
function frameToCropRect(
  sourceWidth: number,
  sourceHeight: number,
  aspect: number,
  offset: number,
): CropRect {
  const sourceAspect = sourceWidth / sourceHeight
  const clamped = Math.min(1, Math.max(0, offset))

  if (sourceAspect > aspect) {
    const width = aspect / sourceAspect
    return { left: clamped * (1 - width), top: 0, width, height: 1 }
  }
  const height = sourceAspect / aspect
  return { left: 0, top: clamped * (1 - height), width: 1, height }
}

/** Signals that this file cannot be framed, so callers can localise the message. */
export class ImageCropError extends Error {}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new ImageCropError('decode failed'))
    image.src = src
  })
}

/**
 * Renders the chosen section at the platform's target resolution, losslessly -
 * the backend performs the one JPEG encode. Throws rather than falling back to the
 * source file, which carries neither the target frame nor the chosen section, so
 * publishing it would silently produce a post the caller never approved.
 */
export async function cropFileToFormat(
  file: File,
  previewUrl: string,
  format: PostFormat,
  offset: number,
): Promise<File> {
  const image = await loadImage(previewUrl)

  const rect = frameToCropRect(
    image.naturalWidth,
    image.naturalHeight,
    format.width / format.height,
    offset,
  )

  const canvas = document.createElement('canvas')
  canvas.width = format.width
  canvas.height = format.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ImageCropError('no 2d context')

  // A fresh canvas is transparent *black* and JPEG has no alpha channel, so
  // without this a transparent PNG publishes as a black rectangle.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  ctx.drawImage(
    image,
    rect.left * image.naturalWidth,
    rect.top * image.naturalHeight,
    rect.width * image.naturalWidth,
    rect.height * image.naturalHeight,
    0,
    0,
    format.width,
    format.height,
  )

  // Lossless: the backend re-encodes to JPEG, and encoding here too would put the
  // pixels through two lossy passes.
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new ImageCropError('encode failed')

  const name = file.name.replace(/\.[^.]+$/, '') || 'image'
  return new File([blob], `${name}.png`, { type: 'image/png' })
}
